import { useAtomValue, atom } from 'jotai';
import { updateQaAutoTranslateConfig } from 'services/api/admin';
import { toUpperCase } from 'utils/toLowerCase';
import { useCurrentConversationIdValue } from '../conversation';
import { ChatPreference, UserDB } from 'lib/db';
import { useLiveQuery } from 'dexie-react-hooks';
import { useCallback, useMemo } from 'react';
import { useRegionLanguageMap } from '../system/useRegionLanguage';
import { useAtomCallback } from 'jotai/utils';

export enum TipsStatus {
  Show,
  Dismissed,
}

const autoTranslateConfigState = atom(false);

export const useAutoTranslateConfig = () => useAtomValue(autoTranslateConfigState);

export const useSetAutoTranslateConfig = () => {
  return useAtomCallback(
    useCallback((_, set, value: boolean, updateService = true) => {
      set(autoTranslateConfigState, value);

      return updateService ? updateQaAutoTranslateConfig(value).catch(() => {}) : Promise.resolve();
    }, []),
  );
};

export const useCurrentChatBuyerLang = (
  region: General.CaselessRegion,
  platform?: General.PlatformUpperCaseTunnel,
) => {
  const preference = useCurrentChatPreferenceValue();
  const regionLanguageMap = useRegionLanguageMap();

  return useMemo(
    () =>
      preference?.buyerLang ||
      (platform === 'FACEBOOK'
        ? regionLanguageMap.FACEBOOK
        : regionLanguageMap[toUpperCase(region)]) ||
      regionLanguageMap.GB,
    [preference?.buyerLang, region, regionLanguageMap, platform],
  );
};

export const useCurrentChatGuaranteedAssign = () => {
  const preference = useCurrentChatPreferenceValue();

  return useMemo(
    () => preference?.grantedAssignTips ?? TipsStatus.Show,
    [preference?.grantedAssignTips],
  );
};

export const useSetCurrentChatPreference = () => {
  const conversationId = useCurrentConversationIdValue();
  return (value: Omit<ChatPreference, 'conversationId'>) => {
    if (conversationId) {
      UserDB.instance?.chatPreference.put(
        {
          ...value,
          conversationId,
        },
        conversationId,
      );
    }
  };
};

export const useCurrentChatPreferenceValue = () => {
  const conversationId = useCurrentConversationIdValue();

  return useLiveQuery(() => {
    if (conversationId) {
      return UserDB.instance?.chatPreference
        .where('conversationId')
        ?.equals(conversationId)
        .first();
    }
  }, [conversationId]);
};
